import { useCallback, useEffect, useRef, useState } from 'react';
import type { PointerEvent } from 'react';
import { Eraser, Undo2 } from 'lucide-react';
import { t } from '../../lib/i18n';
import type { SignatureStrokes } from '../SignatureView';
import { Button } from '../ui';

/** `[x, y, t]`: coordinates in pad units, `t` in milliseconds since the first point of the signature (ADR 0010). */
export type StrokePoint = [number, number, number];
export type SignatureValue = { v: 1; width: number; height: number; strokes: StrokePoint[][] };

/** True when there is at least one stroke with a point. */
export function hasInk(strokes: ReadonlyArray<ReadonlyArray<unknown>>): boolean {
  return strokes.some((s) => s.length > 0);
}

/** Builds the stored value. Returns null when nothing has been drawn. */
export function toSignatureValue(width: number, height: number, strokes: StrokePoint[][]): SignatureValue | null {
  const kept = strokes.filter((s) => s.length > 0);
  return kept.length === 0 ? null : { v: 1, width, height, strokes: kept };
}

/** The value as the JSON text the `signature` file purpose stores (`application/vnd.cloudsign.strokes+json`). */
export function signatureToJson(value: SignatureValue): string {
  return JSON.stringify(value);
}

export interface SignaturePadProps {
  /** Visible label. */
  label: string;
  /** Pad size in its own units (the stored coordinates). Default 480 x 200. */
  width?: number;
  height?: number;
  disabled?: boolean;
  /** Called after each stroke, after Undo and after Clear. `null` when the pad is empty. */
  onChange: (value: SignatureValue | null) => void;
}

/** Draw a signature with a finger, pen or mouse. The output is the vector strokes format, never an image. */
export function SignaturePad({ label, width = 480, height = 200, disabled, onChange }: SignaturePadProps) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const strokes = useRef<StrokePoint[][]>([]);
  const drawing = useRef(false);
  const origin = useRef(0);
  const [inked, setInked] = useState(false);

  const paint = useCallback(() => {
    const ctx = canvas.current?.getContext?.('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, width, height);
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#0f172a';
    for (const s of strokes.current) {
      const first = s[0];
      if (!first) continue;
      ctx.beginPath();
      ctx.moveTo(first[0], first[1]);
      if (s.length === 1) ctx.lineTo(first[0] + 0.01, first[1]);
      for (const p of s.slice(1)) ctx.lineTo(p[0], p[1]);
      ctx.stroke();
    }
  }, [width, height]);

  useEffect(() => paint(), [paint]);

  const publish = () => {
    setInked(hasInk(strokes.current));
    onChange(toSignatureValue(width, height, strokes.current));
  };

  const point = (e: PointerEvent<HTMLCanvasElement>): StrokePoint => {
    const rect = e.currentTarget.getBoundingClientRect();
    const sx = rect.width > 0 ? width / rect.width : 1;
    const sy = rect.height > 0 ? height / rect.height : 1;
    const x = Math.min(width, Math.max(0, (e.clientX - rect.left) * sx));
    const y = Math.min(height, Math.max(0, (e.clientY - rect.top) * sy));
    if (strokes.current.length === 0 || origin.current === 0) origin.current = e.timeStamp;
    return [Math.round(x * 10) / 10, Math.round(y * 10) / 10, Math.max(0, Math.round(e.timeStamp - origin.current))];
  };

  const down = (e: PointerEvent<HTMLCanvasElement>) => {
    if (disabled) return;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    drawing.current = true;
    strokes.current.push([point(e)]);
    paint();
  };
  const move = (e: PointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current) return;
    strokes.current[strokes.current.length - 1]?.push(point(e));
    paint();
  };
  const up = () => {
    if (!drawing.current) return;
    drawing.current = false;
    publish();
  };

  const undo = () => {
    strokes.current.pop();
    if (strokes.current.length === 0) origin.current = 0;
    paint();
    publish();
  };
  const clear = () => {
    strokes.current = [];
    origin.current = 0;
    paint();
    publish();
  };

  return (
    <div className="space-y-2">
      <p className="text-sm font-medium text-slate-800">{label}</p>
      <canvas
        ref={canvas}
        width={width}
        height={height}
        role="img"
        aria-label={inked ? t('p3w.signature.signed', { label }) : t('p3w.signature.blank', { label })}
        className="w-full max-w-xl touch-none rounded-lg border border-slate-300 bg-white"
        style={{ aspectRatio: `${width} / ${height}` }}
        onPointerDown={down}
        onPointerMove={move}
        onPointerUp={up}
        onPointerCancel={up}
      />
      <p className="text-xs text-slate-600">{t('p3w.signature.hint')}</p>
      <div className="flex gap-2">
        <Button disabled={disabled || !inked} onClick={undo}>
          <Undo2 size={16} aria-hidden="true" />
          {t('p3w.signature.undo')}
        </Button>
        <Button disabled={disabled || !inked} onClick={clear}>
          <Eraser size={16} aria-hidden="true" />
          {t('p3w.signature.clear')}
        </Button>
      </div>
    </div>
  );
}

/** Convenience for callers holding the value as a `SignatureStrokes` (for `SignatureView`). */
export function asStrokes(value: SignatureValue): SignatureStrokes {
  return value;
}
